import React, { useMemo, useState } from "react";
import { AlertTriangle, Wrench, ShieldAlert, FileClock, ClipboardCheck, Inbox, CalendarClock } from "lucide-react";
import { Panel, Tag, EmptyState } from "@/components/kit";
import { AlertsPanel } from "@/components/alerts";
import { fmtDate, formatReg, REQUEST_LABEL } from "@/lib/vda";

const MODULE_META = {
  attention_now: { label: "Needs attention now", icon: AlertTriangle },
  forward_service_mot: { label: "Service & MOT coming due", icon: Wrench },
  open_recalls: { label: "Open safety recalls", icon: ShieldAlert },
  contract_ending: { label: "Contracts ending", icon: CalendarClock },
  compliance_expiry: { label: "Compliance expiring", icon: FileClock },
  weekly_checks: { label: "Weekly vehicle checks", icon: ClipboardCheck },
  open_requests: { label: "Open requests", icon: Inbox },
};

function bucketsUpTo(mod, horizons, maxH) {
  // Flatten backend buckets {now, "7", "30", "90"} up to and including maxH (or all when null).
  const keys = ["now", ...horizons.map(String)];
  const items = [];
  for (const k of keys) {
    if (maxH !== null && k !== "now" && Number(k) > maxH) continue;
    (mod[k] || []).forEach((it) => items.push({ ...it, bucket: k }));
  }
  return items.sort((a, b) => (a.days ?? 999) - (b.days ?? 999));
}

const BucketTag = ({ days }) => {
  if (days === undefined || days === null) return null;
  if (days < 0) return <Tag tone="red">Overdue {Math.abs(days)}d</Tag>;
  if (days <= 7) return <Tag tone="red">{days}d</Tag>;
  if (days <= 30) return <Tag tone="amber">{days}d</Tag>;
  return <Tag tone="muted">{days}d</Tag>;
};

export function FleetDashboard({ data, onOpenVehicle }) {
  const { config, modules } = data;
  const horizons = config?.horizons || [7, 30, 90];
  const [horizon, setHorizon] = useState(null); // null = all
  const enabled = config?.modules || Object.keys(MODULE_META);

  const HORIZON_TABS = useMemo(() => {
    const maxH = horizons[horizons.length - 1];
    return [
      { key: "now", label: "Now", max: 0 },
      ...horizons.map((h, i) => ({ key: String(h), label: i === horizons.length - 1 ? `≤${h} days` : `Next ${h}d`, max: h })),
      { key: "all", label: "All", max: null, alt: maxH },
    ];
  }, [horizons]);
  const activeMax = horizon;

  const isEnabled = (k) => enabled.includes(k);
  const open = (reg) => reg && onOpenVehicle && onOpenVehicle(reg);

  return (
    <div data-testid="fleet-dashboard">
      <AlertsPanel />
      {/* Summary strip */}
      <div className="overflow-x-auto border-y border-[hsl(var(--vda-strong-border))] bg-card" data-testid="fd-summary">
        <div className="flex min-w-max">
          {[
            ["Active vehicles", modules.counts.active_vehicles, "green"],
            ["Open requests", modules.counts.open_bookings, "green"],
            ["Attention now", modules.counts.attention, modules.counts.attention > 0 ? "red" : "muted"],
          ].map(([label, value, tone]) => (
            <div key={label} className="flex flex-col justify-center gap-1.5 min-h-[74px] px-5 border-r border-border last:border-r-0" data-testid={`fd-stat-${String(label).toLowerCase().replace(/ /g, "-")}`}>
              <span className="vda-tick text-[hsl(var(--vda-quiet))] whitespace-nowrap">{label}</span>
              <span className={`font-mono text-[22px] leading-none font-semibold tabular-nums ${tone === "red" ? "text-[hsl(var(--vda-red))]" : tone === "muted" ? "text-foreground" : "text-primary"}`}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Urgent — config-driven (thresholds + enabled modules), not a hardcoded banner */}
      {(() => {
        const urgent = (modules.urgent || []).filter((u) =>
          (u.kind === "compliance" && isEnabled("compliance_expiry")) ||
          ((u.kind === "service" || u.kind === "mot") && isEnabled("forward_service_mot")));
        if (!urgent.length) return null;
        return (
          <div className="mt-5 rounded-[8px] border border-[hsl(var(--vda-red))]/40 bg-[hsl(var(--vda-red))]/8 overflow-hidden" data-testid="fd-urgent">
            <div className="px-4 py-2.5 border-b border-[hsl(var(--vda-red))]/25 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--vda-red))]" />
              <span className="font-semibold text-[hsl(var(--vda-red))]">Urgent — action needed now</span>
              <span className="font-mono text-sm text-[hsl(var(--vda-red))]/80">{urgent.length}</span>
            </div>
            {urgent.map((u, i) => (
              <Row key={i} testId={`fd-urgent-${i}`} onClick={() => open(u.registration)}
                left={<><span className="vda-reg text-sm font-bold text-foreground">{u.registration ? formatReg(u.registration) : "Fleet-wide"}</span>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{u.label}</div></>}
                right={<Tag tone={u.state === "overdue" ? "red" : "amber"}>{u.state === "overdue" ? `Overdue ${Math.abs(u.days)}d` : `Due in ${u.days}d`}</Tag>} />
            ))}
          </div>
        );
      })()}

      {/* Horizon selector — WHAT IS COMING NEXT */}
      <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="fd-horizons">
        <span className="vda-tick text-[hsl(var(--vda-quiet))] mr-1">Look ahead</span>
        {HORIZON_TABS.map((t) => {
          const on = (t.key === "all" && horizon === null) || (t.key !== "all" && horizon === t.max);
          return (
            <button key={t.key} data-testid={`fd-horizon-${t.key}`}
              onClick={() => setHorizon(t.key === "all" ? null : t.max)}
              className={`h-9 px-3.5 text-sm rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary text-foreground"}`}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Attention now */}
      {isEnabled("attention_now") && (
        <ModulePanel k="attention_now">
          {modules.attention_now.length === 0 ? <EmptyState title="Nothing needs immediate attention" /> : (
            <div>
              {modules.attention_now.map((b) => (
                <Row key={b.id} testId={`fd-attention-${b.human_reference}`} onClick={() => open(b.registration)}
                  left={<><span className="vda-reg text-sm font-bold text-foreground">{b.registration ? formatReg(b.registration) : "Vehicle pending"}</span>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{REQUEST_LABEL[b.request_type] || b.request_type} · {b.human_reference}</div></>}
                  right={<><Tag tone={b.tone}>{b.note}</Tag><div className="font-mono text-[10px] text-[hsl(var(--vda-quiet))] mt-1">{b.appointment_at ? fmtDate(b.appointment_at) : "No date yet"}</div></>} />
              ))}
            </div>
          )}
        </ModulePanel>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mt-5 items-start">
        {isEnabled("forward_service_mot") && (
          <DateModule k="forward_service_mot" items={bucketsUpTo(modules.forward_service_mot, horizons, activeMax)}
            onOpen={open} render={(it) => (
              <><span className="vda-reg text-sm font-bold text-foreground">{formatReg(it.registration)}</span>
                <span className="ml-2 text-xs text-muted-foreground uppercase">{it.kind}</span></>
            )} meta={(it) => <>{fmtDate(it.due_date)}</>} />
        )}
        {isEnabled("compliance_expiry") && (
          <DateModule k="compliance_expiry" items={bucketsUpTo(modules.compliance_expiry, horizons, activeMax)}
            onOpen={open} render={(it) => (
              <><span className="vda-reg text-sm font-bold text-foreground">{it.registration ? formatReg(it.registration) : "Fleet-wide"}</span>
                <div className="text-xs text-muted-foreground mt-0.5">{it.title || it.record_type}</div></>
            )} meta={(it) => <>{fmtDate(it.expiry_date)}</>} />
        )}
        {isEnabled("contract_ending") && (
          <DateModule k="contract_ending" items={bucketsUpTo(modules.contract_ending, horizons, activeMax)}
            onOpen={open} render={(it) => <span className="vda-reg text-sm font-bold text-foreground">{formatReg(it.registration)}</span>}
            meta={(it) => <>{fmtDate(it.end_date)}</>} />
        )}
        {isEnabled("open_recalls") && (
          <ModulePanel k="open_recalls" count={modules.open_recalls.length}>
            {modules.open_recalls.length === 0 ? <EmptyState title="No open recalls" /> : modules.open_recalls.map((r) => (
              <Row key={r.vehicle_id} testId={`fd-recall-${formatReg(r.registration)}`} onClick={() => open(r.registration)}
                left={<><span className="vda-reg text-sm font-bold text-foreground">{formatReg(r.registration)}</span>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{r.summary || "Open safety recall"}</div></>}
                right={<Tag tone="red">Recall</Tag>} />
            ))}
          </ModulePanel>
        )}
        {isEnabled("weekly_checks") && (
          <ModulePanel k="weekly_checks">
            <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-border" data-testid="fd-weekly-summary">
              {[["Checked this week", modules.weekly_checks.checked_this_week], ["Due", modules.weekly_checks.due_count], ["With defects", modules.weekly_checks.with_defects.length]].map(([l, v]) => (
                <div key={l}><div className="vda-tick text-[hsl(var(--vda-quiet))]">{l}</div><div className="font-mono text-lg font-semibold tabular-nums">{v}</div></div>
              ))}
            </div>
            {modules.weekly_checks.with_defects.length === 0 ? <div className="px-5 py-3 text-sm text-muted-foreground">No outstanding check defects.</div>
              : modules.weekly_checks.with_defects.map((d) => (
                <Row key={d.id} testId={`fd-checkdefect-${formatReg(d.registration)}`} onClick={() => open(d.registration)}
                  left={<span className="vda-reg text-sm font-bold text-foreground">{formatReg(d.registration)}</span>}
                  right={<Tag tone="amber">{d.defect_count} defect{d.defect_count === 1 ? "" : "s"}</Tag>} />
              ))}
          </ModulePanel>
        )}
        {isEnabled("open_requests") && (
          <ModulePanel k="open_requests" count={modules.open_requests.count}>
            {Object.entries(modules.open_requests.by_status).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
              <div key={st} className="flex items-center justify-between px-5 py-2.5 border-b border-border last:border-b-0 text-sm" data-testid={`fd-reqstate-${st.toLowerCase().replace(/ /g, "-")}`}>
                <span className="inline-flex items-center gap-2"><span className="vda-marker" style={{ backgroundColor: "hsl(var(--vda-green))" }} aria-hidden="true" />{st}</span>
                <span className="font-mono font-semibold tabular-nums">{n}</span>
              </div>
            ))}
          </ModulePanel>
        )}
      </div>
    </div>
  );

  function DateModule({ k, items, render, meta, onOpen }) {
    return (
      <ModulePanel k={k} count={items.length}>
        {items.length === 0 ? <EmptyState title="Nothing in this window" /> : items.map((it, i) => (
          <Row key={(it.vehicle_id || it.id || i) + it.bucket + (it.kind || it.record_type || i)} testId={`fd-${k}-${i}`} onClick={() => onOpen(it.registration)}
            left={render(it)} right={<><BucketTag days={it.days} /><div className="font-mono text-[10px] text-[hsl(var(--vda-quiet))] mt-1">{meta(it)}</div></>} />
        ))}
      </ModulePanel>
    );
  }
}

function ModulePanel({ k, count, children }) {
  const Meta = MODULE_META[k];
  const Icon = Meta?.icon;
  return (
    <Panel className="p-0 overflow-hidden self-start" data-testid={`fd-module-${k}`}>
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold inline-flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{Meta?.label || k}</h2>
        {count !== undefined && <span className="font-mono text-sm font-semibold tabular-nums text-muted-foreground">{count}</span>}
      </div>
      <div>{children}</div>
    </Panel>
  );
}

function Row({ left, right, onClick, testId }) {
  return (
    <div role="button" tabIndex={0} data-testid={testId} onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } }}
      className="grid grid-cols-[1fr_auto] gap-3 items-center px-5 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/40 focus:outline-none focus:bg-secondary/40 select-none">
      <div className="min-w-0">{left}</div>
      <div className="text-right">{right}</div>
    </div>
  );
}
